import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Edit, XCircle, AlertTriangle, Upload, Plus, Trash2 } from 'lucide-react';

const BAGGAGE_TYPE_LABELS = {
  kilos: 'Kilos (kg)',
  sacs: 'Sacs à main',
  enveloppes: 'Enveloppes',
  telephones: 'Téléphones',
  ordinateurs: 'Ordinateurs',
  sac_23kg: 'Sacs 23 kg'
};

const ALL_BAGGAGE_TYPES = ['kilos', 'sacs', 'enveloppes', 'telephones', 'ordinateurs', 'sac_23kg'];

const OwnerListingActions = ({ 
  listing, editForm, editDialogOpen, setEditDialogOpen,
  cancelDialogOpen, setCancelDialogOpen, cancelReason, setCancelReason,
  editLoading, cancelLoading, handleEditListing, handleCancelListing,
  updateEditForm, checkIfNeedsReapproval, navigate,
  orderedQuantities = {}, fetchOrderedQuantities,
  API, token, listingId
}) => {
  const [ticketUploading, setTicketUploading] = useState(false);
  const fileInputRef = useRef(null);

  const updateBaggageItem = (index, field, value) => {
    const newItems = [...(editForm.baggage_items || [])];
    if (newItems[index]) {
      newItems[index] = { ...newItems[index], [field]: value };
      updateEditForm('baggage_items', newItems);
    }
  };

  const addBaggageType = (type) => {
    const existing = (editForm.baggage_items || []).find(i => i.type === type);
    if (existing) { toast.error('Ce type existe déjà'); return; }
    const newItems = [...(editForm.baggage_items || []), { type, quantity: 1, price_per_unit: 0, available_quantity: 1 }];
    updateEditForm('baggage_items', newItems);
  };

  const removeBaggageItem = (index) => {
    const item = editForm.baggage_items[index];
    const ordered = orderedQuantities[item?.type] || 0;
    if (ordered > 0) { toast.error(`Impossible: ${ordered} déjà commandé(s)`); return; }
    const newItems = editForm.baggage_items.filter((_, i) => i !== index);
    updateEditForm('baggage_items', newItems);
  };

  const updateCity = (field, index, value) => {
    const cities = [...(editForm[field] || [])];
    cities[index] = value;
    updateEditForm(field, cities);
  };

  const addCity = (field) => {
    const cities = [...(editForm[field] || [])];
    if (cities.length >= 2) { toast.error('Maximum 2 villes'); return; }
    cities.push('');
    updateEditForm(field, cities);
  };

  const removeCity = (field, index) => {
    const cities = (editForm[field] || []).filter((_, i) => i !== index);
    updateEditForm(field, cities);
  };

  const handleTicketUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !API || !token || !listingId) return;
    setTicketUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/listings/${listingId}/upload-ticket`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Billet téléchargé avec succès');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setTicketUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleOpenEditDialog = (open) => {
    setEditDialogOpen(open);
    if (open && fetchOrderedQuantities) fetchOrderedQuantities();
  };

  const needsReapproval = checkIfNeedsReapproval ? checkIfNeedsReapproval() : false;
  const availableToAdd = ALL_BAGGAGE_TYPES.filter(t => !(editForm.baggage_items || []).find(i => i.type === t));

  return (
    <div className="space-y-3">
      {/* Owner label */}
      <p className="text-sm text-muted-foreground text-center font-light">C'est mon annonce</p>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={handleOpenEditDialog}>
        <DialogTrigger asChild>
          <Button className="w-full" variant="outline" size="lg" data-testid="edit-listing-btn">
            <Edit className="w-5 h-5 mr-2" /> Modifier l'annonce
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier l'annonce</DialogTitle>
            <DialogDescription>Les modifications de date, collecte ou livraison nécessitent une ré-approbation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">

            {/* Travel Date */}
            <div className="space-y-1">
              <Label className="text-xs">Date du voyage</Label>
              <Input type="date" value={editForm.travel_date || ''} onChange={(e) => updateEditForm('travel_date', e.target.value)} className="h-9" />
            </div>

            {/* Expiration Date */}
            <div className="space-y-1">
              <Label className="text-xs">Date limite de dépôt des colis</Label>
              <Input type="date" value={editForm.expiration_date || ''} onChange={(e) => updateEditForm('expiration_date', e.target.value)} className="h-9" />
            </div>

            {/* Airline */}
            <div className="space-y-1">
              <Label className="text-xs">Compagnie aérienne</Label>
              <Input value={editForm.airline || ''} onChange={(e) => updateEditForm('airline', e.target.value)} className="h-9" />
            </div>

            {/* Pickup Cities */}
            <div className="space-y-2">
              <Label className="text-xs">Villes de collecte</Label>
              {(editForm.pickup_cities || []).map((city, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={city} onChange={(e) => updateCity('pickup_cities', idx, e.target.value)} placeholder="Ville de collecte" className="h-9 flex-1" />
                  {(editForm.pickup_cities || []).length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-red-500" onClick={() => removeCity('pickup_cities', idx)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              {(editForm.pickup_cities || []).length < 2 && (
                <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => addCity('pickup_cities')}>
                  <Plus className="w-3 h-3 mr-1" /> Ajouter ville
                </Button>
              )}
            </div>

            {/* Delivery Cities */}
            <div className="space-y-2">
              <Label className="text-xs">Villes de livraison</Label>
              {(editForm.delivery_cities || []).map((city, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={city} onChange={(e) => updateCity('delivery_cities', idx, e.target.value)} placeholder="Ville de livraison" className="h-9 flex-1" />
                  {(editForm.delivery_cities || []).length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-red-500" onClick={() => removeCity('delivery_cities', idx)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              {(editForm.delivery_cities || []).length < 2 && (
                <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => addCity('delivery_cities')}>
                  <Plus className="w-3 h-3 mr-1" /> Ajouter ville
                </Button>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <Label className="text-xs">Description / Notes</Label>
              <Textarea value={editForm.description || ''} onChange={(e) => updateEditForm('description', e.target.value)} rows={2} className="text-sm" />
            </div>

            {/* Currency */}
            <div className="space-y-1">
              <Label className="text-xs">Devise</Label>
              <Select value={editForm.currency || 'CAD'} onValueChange={(val) => updateEditForm('currency', val)}>
                <SelectTrigger className="h-9" data-testid="edit-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CAD">CAD - Dollar canadien</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="XAF">XAF - Franc CFA</SelectItem>
                  <SelectItem value="USD">USD - Dollar américain</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Baggage Items */}
            <div className="space-y-3">
              <Label className="text-xs font-medium">Types de bagages</Label>
              {(editForm.baggage_items || []).map((item, index) => {
                const ordered = orderedQuantities[item.type] || 0;
                return (
                  <div key={index} className="p-3 border rounded-lg bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{BAGGAGE_TYPE_LABELS[item.type] || item.type}</Badge>
                        {ordered > 0 && <span className="text-[11px] text-amber-500 font-light">{ordered} commandé(s)</span>}
                      </div>
                      {ordered === 0 && (
                        <Button type="button" variant="ghost" size="sm" className="h-6 px-1.5 text-red-500" onClick={() => removeBaggageItem(index)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Quantité</Label>
                        <Input type="number" min={ordered || 0} value={item.quantity || 0}
                          onChange={(e) => updateBaggageItem(index, 'quantity', parseInt(e.target.value) || 0)} className="h-9" />
                        {ordered > 0 && (
                          <p className="text-[10px] text-muted-foreground">Min: {ordered} · Dispo: {Math.max(0, (item.quantity || 0) - ordered)}</p>
                        )}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Prix/unité ({editForm.currency || listing.currency || 'CAD'})</Label>
                        <Input type="number" min="0" step="0.01" value={item.price_per_unit || 0}
                          onChange={(e) => updateBaggageItem(index, 'price_per_unit', parseFloat(e.target.value) || 0)} className="h-9" />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add new baggage type */}
              {availableToAdd.length > 0 && (
                <Select onValueChange={addBaggageType}>
                  <SelectTrigger className="h-9" data-testid="add-baggage-type-select">
                    <SelectValue placeholder="+ Ajouter un type de bagage" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableToAdd.map(t => (
                      <SelectItem key={t} value={t}>{BAGGAGE_TYPE_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Ticket Upload */}
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-xs font-medium">Billet d'avion / Photo</Label>
              <div className="flex items-center gap-2">
                <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleTicketUpload} className="hidden" id="ticket-upload-edit" />
                <Button type="button" variant="outline" size="sm" className="flex-1" onClick={() => fileInputRef.current?.click()} disabled={ticketUploading}>
                  <Upload className="w-4 h-4 mr-2" />{ticketUploading ? 'Envoi en cours...' : (listing.ticket_file_path || listing.ticket_file) ? 'Remplacer le billet' : 'Télécharger un billet'}
                </Button>
                {(listing.ticket_file_path || listing.ticket_file) && (
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/30">Billet existant</Badge>
                )}
              </div>
            </div>

            {/* Re-approval Warning */}
            {needsReapproval && (
              <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <p className="text-xs text-amber-600">Cette modification nécessitera une ré-approbation par l'admin.</p>
              </div>
            )}

            {/* Submit */}
            <Button className="w-full" onClick={handleEditListing} disabled={editLoading} data-testid="save-edit-btn">
              {editLoading ? 'Enregistrement...' : needsReapproval ? 'Enregistrer (soumis à approbation)' : 'Enregistrer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Upload Button */}
      <div>
        <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleTicketUpload} className="hidden" id="ticket-upload-sidebar" />
        <Button className="w-full" variant="outline" size="lg" onClick={() => fileInputRef.current?.click()} disabled={ticketUploading} data-testid="upload-ticket-btn">
          <Upload className="w-5 h-5 mr-2" />
          {ticketUploading ? 'Envoi en cours...' : (listing.ticket_file_path || listing.ticket_file) ? 'Remplacer le billet' : 'Télécharger mon billet'}
        </Button>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogTrigger asChild>
          <Button className="w-full" variant="outline" size="lg" data-testid="cancel-listing-btn">
            <XCircle className="w-5 h-5 mr-2" /> Annuler l'annonce
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler cette annonce ?</DialogTitle>
            <DialogDescription>Cette action est irréversible. Les acheteurs concernés seront notifiés.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Raison de l'annulation</Label>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Ex: Changement de plans..." className="mt-2" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Retour</Button>
            <Button variant="destructive" onClick={handleCancelListing} disabled={cancelLoading || !cancelReason.trim()} data-testid="confirm-cancel-btn">
              {cancelLoading ? 'Annulation...' : 'Confirmer l\'annulation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to seller's other listings */}
      <Button className="w-full bg-primary/10 text-primary hover:bg-primary/20" variant="ghost" size="sm" onClick={() => navigate('/seller')} data-testid="my-listings-link">
        Mes autres annonces
      </Button>
    </div>
  );
};

export default OwnerListingActions;
